import { useCallback, useEffect, useRef, useState } from "react";

import { faker } from "@faker-js/faker";
import type { Meta, StoryObj } from "@storybook/react";

import {
  fakeSMTEntryProps,
  fakeSMTSingleDayEntriesAccordionProps,
} from "@/__mocks__/SMT";
import type { SMTSingleDayEntriesAccordionProps } from "@/ui/components/smt/ViewSMTEntries/SMTSingleDayEntriesAccordion";
import SMTSingleDayEntriesAccordion from "@/ui/components/smt/ViewSMTEntries/SMTSingleDayEntriesAccordion";
import { Button } from "@/ui/core";
import { Box } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";

type StoryProps = { entryCount: number } & Pick<
  SMTSingleDayEntriesAccordionProps,
  "date"
>;
const StoryMeta: Meta<StoryProps> = {
  title: "Components/SMT/Single Day Entries Accordion",
  component: SMTSingleDayEntriesAccordion,
  argTypes: {
    date: { control: "date" },
    entryCount: { control: "number" },
  },
  args: {
    date: new Date(),
    entryCount: 3,
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const getFakeProps = useCallback(
      () => ({
        ...fakeSMTSingleDayEntriesAccordionProps(),
        entries: faker.helpers.multiple(() => fakeSMTEntryProps(), {
          count: args.entryCount,
        }),
      }),
      [args.entryCount],
    );
    const getFakePropsRef = useRef(getFakeProps);
    useEffect(() => {
      getFakePropsRef.current = getFakeProps;
    }, [getFakeProps]);

    // allow both re-generation of fake props, and use of storybook controls
    const [props, setProps] = useState<SMTSingleDayEntriesAccordionProps>({
      ...getFakePropsRef.current(),
      date: args.date,
    });
    useEffect(() => {
      setProps((current) => ({
        ...current,
        ...getFakePropsRef.current(),
        date: args.date,
      }));
    }, [args]);

    const [regenerationCount, setRegenerationCount] = useState<number>(1);
    useEffect(() => {
      setProps((current) => ({ ...current, ...getFakePropsRef.current() }));
    }, [regenerationCount]);

    return (
      <ModalLayout>
        <Box alignItems="center" paddingBottom={24}>
          <Button
            onPress={() => {
              // force new render, which will force new faker mock calls to generate new data
              setRegenerationCount((oldCount) => oldCount + 1);
            }}
            testID="storybook"
            title="Generate new fake data (+ photos)"
          />
        </Box>
        <SMTSingleDayEntriesAccordion {...props} />
      </ModalLayout>
    );
  },
};

type Story = StoryObj<StoryProps>;

const Default: Story = {};

const Disabled: Story = {
  args: {
    date: new Date(),
    entryCount: 0,
  },
};

export { Default, Disabled };
export default StoryMeta;
